import type { NotificationBase, NotificationState } from '@/core/types';
import { StoreBase } from '@/core/utils/store.utils';
import { toJsDateOrNull } from '@shared/fechas/fecha-like';

/**
 * @file src/core/store/notifications.store.ts
 * @description Store para el manejo de estado de notificaciones
 */

class NotificationsStore extends StoreBase<NotificationState> {
  constructor() {
    super({
      notifications: [],
      unreadCount: 0,
      isFloatingVisible: false,
      currentFloatingNotification: null,
      loading: false,
      lastSync: null
    });
  }

  /**
   * Implementación del método abstracto para obtener el estado inicial
   */
  protected getInitialState(): NotificationState {
    return {
      notifications: [],
      unreadCount: 0,
      isFloatingVisible: false,
      currentFloatingNotification: null,
      loading: false,
      lastSync: null
    };
  }

  /**
   * Agrega una nueva notificación al estado
   */
  addNotification(notification: NotificationBase): void {
    const currentState = this.getState();
    const notifications = [notification, ...currentState.notifications];
    
    this.updateState({
      notifications,
      unreadCount: this.calculateUnreadCount(notifications),
      lastSync: new Date()
    });
  }

  /**
   * Marca una notificación como leída
   */
  markAsRead(notificationId: string): void {
    const currentState = this.getState();
    const notifications = currentState.notifications.map(notification =>
      notification.id === notificationId
        ? { ...notification, leida: true }
        : notification
    );

    this.updateState({
      notifications,
      unreadCount: this.calculateUnreadCount(notifications)
    });
  }

  /**
   * Marca una notificación como mostrada (popup flotante)
   */
  markAsShown(notificationId: string): void {
    const currentState = this.getState();
    const notifications = currentState.notifications.map(notification =>
      notification.id === notificationId
        ? { ...notification, mostrada: true }
        : notification
    );

    this.updateState({
      notifications,
      lastSync: new Date()
    });
  }

  /**
   * Muestra la campanita flotante con una notificación específica
   */
  showFloatingNotification(notification: NotificationBase): void {
    this.updateState({
      isFloatingVisible: true,
      currentFloatingNotification: notification
    });
  }

  /**
   * Oculta la campanita flotante
   */
  hideFloatingNotification(): void {
    this.updateState({
      isFloatingVisible: false,
      currentFloatingNotification: null
    });
  }

  /**
   * Actualiza el estado de completado de una tarea en todas las notificaciones relacionadas
   */
  updateTaskCompletion(tareaId: string, completada: boolean): void {
    const currentState = this.getState();
    const notifications = currentState.notifications.map(notification => {
      if (notification.tareaId === tareaId && 
          (notification.tipo === 'tarea_recordatorio' || notification.tipo === 'tarea_vencida')) {
        return { 
          ...notification, 
          datos: { 
            ...notification.datos, 
            tareaCompletada: completada 
          }
        };
      }
      return notification;
    });

    this.updateState({
      notifications,
      lastSync: new Date()
    });
  }

  /**
   * Establece todas las notificaciones (usado para sincronización inicial)
   */
  setNotifications(notifications: NotificationBase[]): void {
    this.updateState({
      notifications,
      unreadCount: this.calculateUnreadCount(notifications),
      lastSync: new Date(),
      loading: false
    });
  }

  /**
   * Marca el estado como cargando
   */
  setLoading(loading: boolean): void {
    this.updateState({ loading });
  }

  /**
   * Elimina notificaciones expiradas
   */
  removeExpiredNotifications(): void {
    const currentState = this.getState();
    const now = new Date();
    const notifications = currentState.notifications.filter(notification => {
      if (!notification.fechaExpiracion) return true;
      const expiration = notification.fechaExpiracion instanceof Date 
        ? notification.fechaExpiracion 
        : notification.fechaExpiracion.toDate();
      return expiration > now;
    });

    if (notifications.length !== currentState.notifications.length) {
      this.updateState({
        notifications,
        unreadCount: this.calculateUnreadCount(notifications)
      });
    }
  }

  /**
   * Obtiene notificaciones no mostradas como popup flotante
   */
  getUnshownNotifications(): NotificationBase[] {
    return this.getState().notifications.filter(n => !n.mostrada && !n.leida);
  }

  /**
   * Obtiene el número de notificaciones pendientes por mostrar
   */
  getPendingFloatingCount(): number {
    return this.getUnshownNotifications().length;
  }

  /**
   * Calcula el número de notificaciones no leídas
   * Solo cuenta las notificaciones que ya deberían mostrarse (vencidas o programadas para ahora)
   * IMPORTANTE: La campanita solo aparece cuando fechaProgramada <= ahora
   */
  private calculateUnreadCount(notifications: NotificationBase[]): number {
    const now = new Date();
    
    const count = notifications.filter(n => {
      if (n.leida) return false;
      
      // Convertir fechaProgramada a Date si es necesario
      const fechaProgramada = toJsDateOrNull(n.fechaProgramada);
      
      // REGLA PRINCIPAL: Si hay fechaProgramada, solo contar si ya pasó
      // Esto aplica para TODAS las notificaciones programadas (recordatorios, vencimientos, etc.)
      if (fechaProgramada) {
        const shouldShow = fechaProgramada <= now;
        if (!shouldShow) {
          // Notificación programada para el futuro, no contar aún
          return false;
        }
        // La fechaProgramada ya pasó, contar la notificación
        return true;
      }
      
      // Si NO hay fechaProgramada, es una notificación inmediata (como tarea_asignada)
      // Estas se cuentan siempre (usando fechaCreacion)
      return true;
    }).length;
    
    return count;
  }

  /**
   * Limpia el estado (usado en logout)
   */
  clearState(): void {
    this.resetState();
  }
}

export const notificationsStore = new NotificationsStore();